import React, { useEffect, useState } from 'react';
import { api } from '../../api';
import { fmtDate } from '../../dateUtil';
import { STEP_LABELS } from '../../stepFlows';
import { formatCurrency } from '../../currencyUtil';

const STEP_STYLES = {
  awaiting_payment:     'bg-amber-50 text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:ring-amber-800',
  payment_confirmed:    'bg-teal-50 text-teal-700 ring-1 ring-teal-200 dark:bg-teal-900/30 dark:text-teal-400 dark:ring-teal-800',
  awaiting_inspection:  'bg-sky-50 text-sky-700 ring-1 ring-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:ring-sky-800',
  inspection_scheduled: 'bg-blue-50 text-blue-700 ring-1 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:ring-blue-800',
  awaiting_pickup:      'bg-purple-50 text-purple-700 ring-1 ring-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:ring-purple-800',
  closed:               'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:ring-emerald-800',
  cancelled:            'bg-red-50 text-red-600 ring-1 ring-red-200 dark:bg-red-900/30 dark:text-red-400 dark:ring-red-800',
};

const CSV_COLUMNS = [
  ['Deal', t => t.deal_name || `Deal #${t.deal_id}`],
  ['Vehicle', t => carLabel(t)],
  ['Buyer Account', t => t.buyer_account || ''],
  ['Buyer Lot', t => t.buyer_location || ''],
  ['Buyer Name', t => [t.buyer_fname, t.buyer_lname].filter(Boolean).join(' ')],
  ['Seller Account', t => t.seller_account || ''],
  ['Seller Lot', t => t.seller_location || ''],
  ['Bid Price', t => t.bid_price ?? ''],
  ['Step', t => STEP_LABELS[t.step] || t.step],
  ['Payment Due Date', t => t.payment_due_date || ''],
  ['Created At', t => t.created_at || ''],
  ['Closed At', t => t.closed_at || ''],
];

function carLabel(t) {
  return [t.year, t.make, t.model, t.trim].filter(Boolean).join(' ') || t.vin || `Car #${t.car_id}`;
}

function money(n) {
  if (n == null) return '—';
  return `$${formatCurrency(n)}`;
}

function csvEscape(v) {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(rows) {
  const header = CSV_COLUMNS.map(([label]) => csvEscape(label)).join(',');
  const lines = rows.map(t => CSV_COLUMNS.map(([, get]) => csvEscape(get(t))).join(','));
  const csv = [header, ...lines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Transactions() {
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState(false);
  const [search, setSearch] = useState('');
  const [stepFilter, setStepFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/transactions${history ? '?history=1' : ''}`)
      .then(setTxns)
      .finally(() => setLoading(false));
  }, [history]);

  const steps = [...new Set(txns.map(t => t.step))];

  const filtered = txns.filter(t => {
    if (stepFilter && t.step !== stepFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      t.deal_name?.toLowerCase().includes(q) ||
      t.vin?.toLowerCase().includes(q) ||
      t.buyer_account?.toLowerCase().includes(q) ||
      t.seller_account?.toLowerCase().includes(q) ||
      `${t.buyer_fname} ${t.buyer_lname}`.toLowerCase().includes(q)
    );
  });

  if (loading) return (
    <div className="flex items-center justify-center h-48">
      <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by deal, VIN, buyer, or seller…"
          className="w-full max-w-sm text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 placeholder-slate-400"
        />
        <select
          value={stepFilter}
          onChange={e => setStepFilter(e.target.value)}
          className="text-sm border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200"
        >
          <option value="">All steps</option>
          {steps.map(s => (
            <option key={s} value={s}>{STEP_LABELS[s] || s}</option>
          ))}
        </select>
        <button
          onClick={() => setHistory(h => !h)}
          className={`text-sm px-3 py-2 rounded-lg border ${
            history
              ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-300'
              : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300'
          }`}
        >
          {history ? 'Showing history' : 'Showing active'}
        </button>
        <button
          onClick={() => exportCsv(filtered)}
          disabled={!filtered.length}
          className="text-sm px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed ml-auto"
        >
          Export CSV
        </button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/50">
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Deal</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Vehicle</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Buyer</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Seller</th>
              <th className="py-2.5 px-4 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Price</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Step</th>
              <th className="py-2.5 px-4 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {history ? 'Closed' : 'Payment Due'}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id} className="border-t border-slate-100 dark:border-slate-800">
                <td className="py-3 px-4">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                    {t.deal_name || <span className="text-slate-400 italic">Unnamed #{t.deal_id}</span>}
                  </p>
                </td>
                <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                  <p>{carLabel(t)}</p>
                  {t.vin && <p className="text-xs text-slate-400">{t.vin}</p>}
                </td>
                <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                  <p>{t.buyer_account || <span className="text-slate-400">—</span>}</p>
                  {t.buyer_location && <p className="text-xs text-slate-400">{t.buyer_location}</p>}
                </td>
                <td className="py-3 px-4 text-sm text-slate-600 dark:text-slate-300">
                  <p>{t.seller_account || <span className="text-slate-400">—</span>}</p>
                  {t.seller_location && <p className="text-xs text-slate-400">{t.seller_location}</p>}
                </td>
                <td className="py-3 px-4 text-sm text-right text-slate-600 dark:text-slate-300">{money(t.bid_price)}</td>
                <td className="py-3 px-4">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STEP_STYLES[t.step] || ''}`}>
                    {STEP_LABELS[t.step] || t.step}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-slate-500 dark:text-slate-400">
                  {history ? (t.closed_at ? fmtDate(t.closed_at) : '—') : (t.payment_due_date ? fmtDate(t.payment_due_date) : '—')}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-slate-400">
                  {search || stepFilter ? 'No matching transactions' : history ? 'No closed or cancelled transactions' : 'No active transactions'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {txns.length === 1000 && (
          <p className="text-xs text-center text-slate-400 py-2 border-t border-slate-100 dark:border-slate-800">
            Showing first 1,000 transactions
          </p>
        )}
      </div>
    </div>
  );
}
